import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { Close as CloseIcon } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import {
  withCacheBuster,
  type FaceRecognition,
} from "./FaceRecognitionTable.tsx";
import { formatDateDMY } from "../../utils/dateFormat.ts";
import React from "react";

interface ViewFaceDialogProps {
  open: boolean;
  onClose: () => void;
  face: FaceRecognition | null;
  /** Table row id, e.g. FR-001 (matches list/grid ID column). */
  displayId?: string | null;
}

const PHOTO_LABELS: {
  key: keyof NonNullable<FaceRecognition["images"]>;
  label: string;
}[] = [
  { key: "front", label: "Front" },
  { key: "leftProfile", label: "Left Profile" },
  { key: "rightProfile", label: "Right Profile" },
  { key: "upFace", label: "Up Face" },
  { key: "downFace", label: "Down Face" },
];

function formatExpirationDate(value: Date | string): string {
  if (value instanceof Date) return formatDateDMY(value);
  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return formatDateDMY(d);
    return value;
  }
  return "—";
}

export function ViewFaceDialog({
  open,
  onClose,
  face,
  displayId,
}: ViewFaceDialogProps) {
  if (!face) return null;

  const idShown =
    displayId?.trim() || (/^FR-\d{3}$/.test(face.id ?? "") ? face.id : face.id);
  const typeLabel = face.department?.trim() || "—";

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") onClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#F2F2F2",
          py: 2,
          px: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 500,
            color: "#212121",
          }}
        >
          Face Recognition Details
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#4A5565" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 5, overflowY: "auto", overflowX: "hidden" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Face photos - full width of popup, grid layout */}
          <Box sx={{ width: "calc(100% + 48px)", mx: -3 }}>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                mt: 1.5,
                mb: 1.5,
                // px: 3,
              }}
            >
              Face Photos
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: 2,
                width: "100%",
              }}
            >
              {PHOTO_LABELS.map(({ key, label }) => {
                const rawSrc =
                  face.images?.[key] ??
                  (key === "front" ? face.image : undefined);
                const src = rawSrc?.startsWith("blob:")
                  ? rawSrc
                  : (withCacheBuster(rawSrc, face.imageVersion) ?? rawSrc);
                const isValidUrl = src && !String(src).startsWith("blob:");
                return (
                  <Box key={key} sx={{ textAlign: "center", width: "100%" }}>
                    {isValidUrl ? (
                      <Box
                        component="img"
                        src={src}
                        alt={label}
                        sx={{
                          width: "100%",
                          aspectRatio: "1",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #E5E7EB",
                          display: "block",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "1",
                          borderRadius: "8px",
                          border: "1px solid #E5E7EB",
                          bgcolor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#9CA3AF",
                            fontFamily: "Roboto, sans-serif",
                          }}
                        >
                          No image
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#374151",
                        mt: 0.75,
                        lineHeight: 1.4,
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* All form details */}
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                mb: 1.5,
              }}
            >
              Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#9E9E9E",
                    mb: 0.5,
                  }}
                >
                  ID
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "16px",
                    // fontWeight: 600,
                    // color: "#2932E5",
                    letterSpacing: "0.02em",
                  }}
                >
                  {idShown}
                </Typography>
                {/* {!/^FR-\d{3}$/.test(face.id ?? "") && (
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      // color: "#9CA3AF",
                      mt: 0.5,
                      wordBreak: "break-all",
                    }}
                  >
                    Record: {face.id}
                  </Typography>
                )} */}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#9E9E9E",
                    mb: 0.5,
                  }}
                >
                  Type
                </Typography>
                <Chip
                  label={typeLabel}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255, 179, 128, 0.2)",
                    color: "#C2410C",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    fontWeight: 600,
                    height: 26,
                    borderRadius: "4px",
                  }}
                />
              </Box>
              <DetailRow label="Name" value={face.name} />
              <DetailRow label="Title" value={face.title} />
              <DetailRow label="Company" value={face.company ?? "—"} />
              <DetailRow label="Email" value={face.email ?? "—"} />
              <DetailRow label="Phone" value={face.phone ?? "—"} />
              <DetailRow
                label="Expiration Date"
                value={formatExpirationDate(face.expirationDate)}
              />
              {face.description != null && face.description !== "" && (
                <Box
                  sx={{ gridColumn: "1 / -1", width: "100%", maxWidth: "100%" }}
                >
                  <DetailRow
                    label="Description"
                    value={face.description}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#F2F2F2" }}>
        <Button
          onClick={onClose}
          sx={{
            bgcolor: "#2932E5",
            border: "0.8px solid #2932E5",
            borderRadius: "8px",
            color: "#fff",
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            px: 3,
            "&:hover": { bgcolor: "#1f28b8" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: string;
  fullWidth?: boolean;
}) {
  return (
    <Box sx={fullWidth ? { width: "100%", maxWidth: "100%" } : undefined}>
      <Typography
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: "12px",
          fontWeight: 500,
          color: "#9E9E9E",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: "14px",
          color: "#4A5565",
          wordBreak: "break-word",
          ...(fullWidth && {
            display: "block",
            width: "100%",
            maxWidth: "100%",
          }),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
